/**
 * Card validation logic for CardElement
 */

export class CardValidator {
  private skipLuhnValidation: boolean;

  constructor(skipLuhnValidation: boolean = false) {
    this.skipLuhnValidation = skipLuhnValidation;
  }

  /**
   * Validate card number
   */
  validateCardNumber(value: string): string | null {
    const cleaned = value.replace(/\s/g, '');

    if (!cleaned) {
      return null; // Empty is okay until user starts typing
    }

    if (!/^\d+$/.test(cleaned)) {
      return 'Please enter a valid card number.';
    }

    if (cleaned.length < 13) {
      return 'Card number is too short.';
    }

    if (cleaned.length > 19) {
      return 'Card number is too long.';
    }

    // Basic Luhn check (skip if disabled for testing)
    if (!this.skipLuhnValidation && !this.luhnCheck(cleaned)) {
      return 'Please check your card number.';
    }

    return null;
  }

  /**
   * Validate expiry date (MM/YY format)
   */
  validateExpiry(value: string): string | null {
    if (!value) {
      return null; // Empty is okay
    }

    const parts = value.split('/');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return 'Please enter expiry as MM/YY.';
    }

    const month = parseInt(parts[0], 10);
    const year = parseInt('20' + parts[1], 10);

    if (isNaN(month) || month < 1 || month > 12) {
      return 'Please enter a valid month.';
    }

    if (isNaN(year)) {
      return 'Please enter a valid year.';
    }

    // Check if expired
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return 'This card has expired.';
    }

    return null;
  }

  /**
   * Validate CVC code
   */
  validateCvc(value: string): string | null {
    if (!value) {
      return null; // Empty is okay
    }

    if (!/^\d+$/.test(value)) {
      return 'Please enter a valid security code.';
    }

    if (value.length < 3) {
      return 'Security code is too short.';
    }

    if (value.length > 4) {
      return 'Security code is too long.';
    }

    return null;
  }

  /**
   * Luhn algorithm for card number validation
   * https://en.wikipedia.org/wiki/Luhn_algorithm
   */
  private luhnCheck(cardNumber: string): boolean {
    let sum = 0;
    let isEven = false;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber.charAt(i), 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }
}